import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { countries, getCountryByCode } from '../data/countries';
import { getGeoLocation, submitLead, getDialCodeFromCountry, navigateToApplication } from '../services/api';

const RegistrationModal = ({ open, onOpenChange }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    countryCode: '+1',
    message: ''
  });

  // Fetch geolocation on component mount
  useEffect(() => {
    const fetchGeoLocation = async () => {
      const result = await getGeoLocation();
      if (result.success && result.data) {
        setGeoData(result.data);
        
        // Set default country code based on geo location
        if (result.data.country) {
          const dialCode = getDialCodeFromCountry(result.data.country);
          setFormData(prev => ({ ...prev, countryCode: `+${dialCode}` }));
        }
      }
    };
    
    fetchGeoLocation();
  }, []);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    const currentCountry = getCountryByCode(formData.countryCode);
    
    // Limit to max length for selected country
    if (value.length <= currentCountry.maxLength) {
      setFormData({ ...formData, phone: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.email || !formData.phone || !formData.message) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Get country code from dial code
      const dialCode = parseInt(formData.countryCode.replace('+', ''));
      const country = geoData?.country || 'US';
      const ipAddress = geoData?.ipAddress || '0.0.0.0';

      // Submit lead using centralized API service
      const result = await submitLead({
        name: formData.fullName,
        email: formData.email,
        countryDialCode: dialCode,
        country: country,
        phoneNumber: formData.phone, // Only digits, no prefix
        ipAddress: ipAddress,
        description: formData.message,
        pageUrl: window.location.href
      });

      if (result.success) {
        toast({
          title: 'Success!',
          description: result.message || 'Your registration has been submitted.',
        });

        // Reset form and close modal
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          countryCode: formData.countryCode,
          message: ''
        });

        onOpenChange(false);

        // Redirect to application page
        setTimeout(() => {
          navigateToApplication(result.customerId);
        }, 1500);

      } else {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive'
        });
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            Get Started
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Fill out the form below and our team will contact you within 24 hours
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
            <Input
              type="text"
              placeholder="John Doe"
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              className="h-11"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
            <Input
              type="email"
              placeholder="john@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="h-11"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
            <div className="flex gap-2">
              <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value, phone: '' })}>
                <SelectTrigger className="w-28 h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {countries.map((country, index) => (
                    <SelectItem key={index} value={country.code}>
                      {country.flag} {country.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="tel"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handlePhoneChange}
                className="flex-1 h-11"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Message *</label>
            <Textarea
              placeholder="Tell us about your needs..."
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="min-h-[120px]"
              required
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-11 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RegistrationModal;
